import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from './config.service';
import { timeout, share } from 'rxjs/operators';
import {
    UserDetail,
    UserManagementApiClient as UserManagementApiClientV2,
    UpdateUserRolesRequest,
    UserSummaryV2,
    PatchUserDetails,
    Role,
    CreateUserRequestV2,
    PatchUser
} from '../api-module/user-management-api-client-v2';

const HTTP_TIMEOUT_MSECONDS = 20000;

@Injectable({
    providedIn: 'root'
})
export class UserSettingsService {
    private readonly userManagementApiBaseUrl: string;

    constructor(private readonly http: HttpClient, private readonly userManagementApiClientV2: UserManagementApiClientV2) {
        this.userManagementApiBaseUrl = `${ConfigService.settings.apiBaseUrl}/user-management-api/`;
    }

    getUsersByOrgV2(orgCode: string): Observable<UserSummaryV2[]> {
        return this.http
            .get<UserSummaryV2[]>(this.userManagementApiBaseUrl + 'v2/users?orgCode=' + orgCode)
            .pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    getUsersByGuidV2(id: string): Observable<UserDetail> {
        return this.userManagementApiClientV2.usersV_GetById(id).pipe(timeout(HTTP_TIMEOUT_MSECONDS), share());
    }

    createUserV2(user: CreateUserRequestV2) {
        return this.http.post(this.userManagementApiBaseUrl + 'v2/users', user).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    updateUserV2(id: string, user: PatchUser) {
        return this.userManagementApiClientV2.usersV_Patch(id, user).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    resetUserMfa(id: string): Observable<void> {
        return this.userManagementApiClientV2.usersV_ResetUserMfa(id).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    createUserOrg(id: string, payload: { orgCode: string; defaultSiteId?: number }) {
        return this.http.post(`${this.userManagementApiBaseUrl}v2/users/${id}/user-orgs`, payload).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    updateUserOrgRoles(id: string, orgCode: string, request: UpdateUserRolesRequest) {
        return this.userManagementApiClientV2.userOrgsV_UpdateUserRoles(id, orgCode, request).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    updateUserDefaultSite(id: string, orgCode: string, request: PatchUserDetails) {
        return this.userManagementApiClientV2.userOrgsV_UpdateUserDetails(id, orgCode, request).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    removeUser(id: string, orgCode: string) {
        return this.userManagementApiClientV2.usersV_DeleteUserRoles(id, orgCode).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    removeUserV2(id: string, orgCode: string) {
        return this.userManagementApiClientV2.usersV_DeleteUserRoles(id, orgCode).pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    getRoles() {
        return this.http.get<Role[]>(this.userManagementApiBaseUrl + 'v2/roles').pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    getResetPasswordLink() {
        return this.userManagementApiClientV2.usersV_GetResetPasswordLink().pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }

    sendChangedPasswordConfirmationEmail() {
        return this.userManagementApiClientV2.usersV_SendChangedPasswordConfirmationEmail().pipe(timeout(HTTP_TIMEOUT_MSECONDS));
    }
}
